import React, { Fragment, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { IoMdArrowRoundBack } from "react-icons/io";
import { FaSpinner, FaSave, FaImage, FaBox, FaArrowLeft, FaStar, FaRegStar } from "react-icons/fa";
import LoadingScreen from '../../components/LoadingScreen';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';

function EditProductPage() {
    const [productData, setProductData] = useState(null);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const { productId } = useParams();
    const [uploadingStates, setUploadingStates] = useState({
        image1: false,
        image2: false,
        image3: false,
        image4: false,
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();
    const token = localStorage.getItem('authToken');

    const handleApiError = (error) => {
        console.error('API Error:', error);
        const backendError = error.response?.data?.error;
        const msg = backendError 
            ? `${error.response.data.message} (${backendError})` 
            : (error.response?.data?.message || 'Something went wrong. Please try again.');
        alert(msg);
    };

    const getCategories = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/categorydata`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCategories(response.data);
        } catch (error) {
            handleApiError(error);
        }
    };

    const getBrands = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/branddata`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBrands(response.data);
        } catch (error) {
            handleApiError(error);
        }
    };

    const getProductData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/productdata/${productId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const product = response.data.productData;
            // Ensure brand stays as name (add form stores name, not id)
            setProductData({
                ...product,
                brand: product.brand?.name || product.brand,
                originalPrice: product.originalPrice || product.price || 0,
                rating: product.rating || 0,
                stock: product.stock ?? 0,
                description: (product.description || '')
                    .replace(/^About this item\s*(?:\\n|[\r\n])*\s*(?:•)?\s*/i, '')
                    .replace(/\\n/g, '\n')
            });
        } catch (error) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleReplaceImage = async (event) => {
        const imageKey = event.target.name;
        const file = event.target.files[0];
        if (!file) return;

        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
            alert('Only JPG, PNG, or WebP images are allowed.');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            alert('File size must be under 5MB.');
            return;
        }

        try {
            setUploadingStates((prev) => ({ ...prev, [imageKey]: true }));
            const formData = new FormData();
            formData.append('image', file);
            formData.append('imageKey', imageKey);
            formData.append('productId', productId);
            const response = await axios.post(`${API_BASE_URL}/replace-img`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            // Normalize path: ensure leading slash for relative paths
            let imgPath = response.data.imagePath;
            if (imgPath && !imgPath.startsWith('http') && !imgPath.startsWith('/')) {
                imgPath = '/' + imgPath;
            }
            setProductData((prev) => ({ ...prev, [imageKey]: imgPath }));
            alert("Image updated successfully!");
        } catch (error) {
            handleApiError(error);
        } finally {
            setUploadingStates((prev) => ({ ...prev, [imageKey]: false }));
        }
    };

    const handleChange = (event) => {
        setProductData({
            ...productData,
            [event.target.name]: event.target.value,
        });
    };

    const validateFields = () => {
        const requiredFields = [
            'name', 'brand', 'originalPrice', 'price', 'category', 'quantity', 'unit',
            'stock', 'image1' // Only first image is required
        ];

        for (let field of requiredFields) {
            if (!productData || productData[field] === undefined || productData[field] === null || productData[field].toString().trim() === '') {
                alert(`${field.charAt(0).toUpperCase() + field.slice(1)} is required`);
                return false;
            }
        }
        return true;
    };

    const handleUpdateProduct = async () => {
        if (!validateFields()) return;

        try {
            setSaving(true);
            const response = await axios.patch(`${API_BASE_URL}/update-product`, productData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(response.data.message);
            navigate(-1);
        } catch (error) {
            handleApiError(error);
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        getCategories();
        getProductData();
        getBrands();
    }, []);

    if (loading) {
        return (
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 'calc(100vh - 120px)' }}>
                    <LoadingScreen />
                    <p className="mt-3 text-muted small fw-semibold" style={{ letterSpacing: '0.5px' }}>Loading product details...</p>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">
                {/* Header */}
                <div className="row">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="section-title">
                                <h4 className="mt-1">EDIT PRODUCT</h4>
                                <p className="text-muted p-0 m-0">Update product information and images</p>
                            </div>
                            <button 
                                type="button"
                                className="back-btn-pill"
                                onClick={() => navigate(-1)}
                                title="Back to Products"
                            >
                                <FaArrowLeft />
                                Back to Products
                            </button>
                        </div>
                    </div>
                </div>

                {/* Product Form */}
                <div className="row mt-0">
                    <div className="col-12">
                        <div className="details-card-custom edit-product-card-custom">
                            <div className="details-card-header">
                                <h5>
                                    <FaBox className="text-danger me-2" />
                                    Product Information
                                </h5>
                            </div>
                            <div className="details-card-body p-4">
                                <div className="row">
                                    {/* Left Column - Basic Info */}
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label form-label-custom">Product Name</label>
                                            <input 
                                                type="text" 
                                                className="form-control form-control-custom" 
                                                name="name" 
                                                value={productData?.name || ''} 
                                                onChange={handleChange} 
                                                placeholder="Enter product name"
                                            />
                                        </div>
                                        
                                        <div className="mb-3">
                                            <label className="form-label form-label-custom">Brand</label>
                                            <select 
                                                className="form-select form-control-custom" 
                                                name="brand" 
                                                value={productData?.brand || ''} 
                                                onChange={handleChange}
                                            >
                                                <option value="">Select Brand</option>
                                                {brands.map((brand) => (
                                                    <option key={brand._id} value={brand.name}>{brand.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        
                                        <div className="mb-3">
                                            <label className="form-label form-label-custom">Category</label>
                                            <select 
                                                className="form-select form-control-custom" 
                                                name="category" 
                                                value={productData?.category || ''} 
                                                onChange={handleChange}
                                            >
                                                <option value="">Select Category</option>
                                                {categories.map((category) => (
                                                    <option key={category._id} value={category.name}>{category.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        
                                        <div className="mb-3">
                                            <label className="form-label form-label-custom">Original Price (MRP)</label>
                                            <input
                                                type="number"
                                                min={0}
                                                className="form-control form-control-custom"
                                                name="originalPrice"
                                                value={productData?.originalPrice || ''}
                                                onChange={handleChange}
                                                placeholder="Enter original price"
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label form-label-custom">Discounted Price</label>
                                            <input
                                                type="number"
                                                min={0}
                                                className="form-control form-control-custom"
                                                name="price"
                                                value={productData?.price || ''}
                                                onChange={handleChange}
                                                placeholder="Enter discounted price"
                                            />
                                            {productData?.originalPrice > 0 && productData?.price > 0 && productData.originalPrice > productData.price && (
                                                <small className="text-success d-block mt-1">
                                                    Discount: {Math.round(((productData.originalPrice - productData.price) / productData.originalPrice) * 100)}%
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Right Column - Quantity, Unit, Stock, Description */}
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label form-label-custom">Quantity (Pack Size)</label>
                                            <input 
                                                type="text" 
                                                className="form-control form-control-custom" 
                                                name="quantity" 
                                                value={productData?.quantity || ''} 
                                                onChange={handleChange} 
                                                placeholder="Enter pack quantity (e.g. 1, 400)"
                                            />
                                        </div>
                                        
                                        <div className="mb-3">
                                            <label className="form-label form-label-custom">Unit</label>
                                            <div className="input-group">
                                                <input 
                                                    type="text" 
                                                    className="form-control form-control-custom" 
                                                    name="unit" 
                                                    value={productData?.unit || ''} 
                                                    onChange={handleChange} 
                                                    placeholder="Type or select unit"
                                                />
                                                <button 
                                                    className="btn btn-outline-secondary dropdown-toggle" 
                                                    style={{ borderRadius: 0, border: '1px solid #cbd5e1', borderLeft: 'none' }}
                                                    type="button" 
                                                    data-bs-toggle="dropdown" 
                                                    aria-expanded="false"
                                                >
                                                </button>
                                                <ul className="dropdown-menu dropdown-menu-end rounded-0" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                    {['cm', 'g', 'gm', 'jar', 'kg', 'l', 'lkg', 'Ltr', 'ml', 'ml.', 'mtr', 'p', 'pack', 'pc', 'pck', 'pcs'].map((u) => (
                                                        <li key={u}>
                                                            <button 
                                                                className="dropdown-item" 
                                                                type="button" 
                                                                onClick={() => setProductData(prev => ({ ...prev, unit: u }))}
                                                            >
                                                                {u}
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label form-label-custom">Stock</label>
                                            <input 
                                                type="number" 
                                                className="form-control form-control-custom" 
                                                name="stock" 
                                                value={productData?.stock ?? '0'} 
                                                onChange={handleChange} 
                                                placeholder="Enter stock level"
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label form-label-custom">Rating</label>
                                            <div className="d-flex align-items-center gap-1">
                                                {[1,2,3,4,5].map(i => (
                                                    <span
                                                        key={i}
                                                        style={{cursor:'pointer'}}
                                                        onClick={()=> setProductData(prev=> ({...prev, rating: i}))}
                                                        title={`${i} star${i>1?'s':''}`}
                                                    >
                                                        {(productData?.rating || 0) >= i ? <FaStar className="text-warning" /> : <FaRegStar className="text-warning" />}
                                                    </span>
                                                ))}
                                                <small className="ms-2 text-muted">{productData?.rating || 0} / 5</small>
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label form-label-custom">Description</label>
                                            <textarea 
                                                rows={4} 
                                                className="form-control form-control-custom" 
                                                name="description" 
                                                value={productData?.description || ''} 
                                                onChange={handleChange} 
                                                placeholder="Enter product description"
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Product Images */}
                <div className="row mt-0">
                    <div className="col-12">
                        <div className="details-card-custom edit-product-card-custom">
                            <div className="details-card-header">
                                <h5 className="mb-0">
                                    <FaImage className="text-primary me-2" />
                                    Product Images
                                </h5>
                                <small className="text-muted font-monospace" style={{ fontSize: '0.75rem' }}>*First image is the cover image*</small>
                            </div>
                            <div className="details-card-body p-0">
                                <div className="row g-0">
                                    {['image1', 'image2', 'image3', 'image4'].map((imageKey, index) => (
                                        <div className="col-md-6 col-lg-3" key={imageKey}>
                                            <div className="p-3 bg-light" style={{ borderRadius: 0, borderRight: '1px solid #cbd5e1', borderBottom: '1px solid #cbd5e1' }}>
                                                <label className="form-label form-label-custom mb-2">
                                                    Image {index + 1}
                                                </label>
                                                
                                                <div className="text-center mb-3 d-flex align-items-center justify-content-center bg-white border" style={{ height: '130px', borderColor: '#cbd5e1' }}>
                                                    {uploadingStates[imageKey] ? (
                                                        <div className="d-flex align-items-center justify-content-center">
                                                            <FaSpinner className="spinner-border spinner-border-sm me-2 text-danger" />
                                                            <small className="text-muted">Uploading...</small>
                                                        </div>
                                                    ) : (
                                                        productData?.[imageKey] ? (
                                                            <img
                                                                src={
                                                                    productData[imageKey].startsWith('http')
                                                                        ? productData[imageKey]
                                                                        : `${API_BASE_URL}${productData[imageKey].startsWith('/') ? productData[imageKey] : '/' + productData[imageKey]}`
                                                                }
                                                                alt={`${productData?.name} - Image ${index + 1}`}
                                                                className="img-fluid"
                                                                style={{ maxHeight: '110px', width: 'auto', objectFit: 'contain' }}
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    e.target.nextSibling.style.display = 'block';
                                                                }}
                                                            />
                                                        ) : (
                                                            <span className="text-muted small">No image uploaded</span>
                                                        )
                                                    )}
                                                    <div className="text-muted small mt-1" style={{ display: 'none' }}>
                                                        No image available
                                                    </div>
                                                </div>
                                                
                                                <input 
                                                    type="file" 
                                                    className="form-control form-control-custom" 
                                                    name={imageKey} 
                                                    accept="image/*" 
                                                    onChange={handleReplaceImage}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="row mt-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-end gap-3">
                            <button 
                                className="btn btn-outline-secondary rounded-0 px-4" 
                                onClick={() => navigate(-1)}
                            >
                                Cancel
                            </button>
                            <button 
                                className="save-brand-btn px-4" 
                                onClick={handleUpdateProduct} 
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <FaSpinner className="spinner-border spinner-border-sm me-2" />
                                        Updating...
                                    </>
                                ) : (
                                    <>
                                        <FaSave className="me-2" />
                                        Update Product
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .edit-product-card-custom {
                    margin-left: -1rem !important;
                    margin-right: -1rem !important;
                    margin-bottom: 0 !important;
                    border-left: none !important;
                    border-right: none !important;
                    border-top: none !important;
                    border-bottom: 1px solid #cbd5e1 !important;
                    border-radius: 0 !important;
                }

                .form-label-custom {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
                    font-size: 0.72rem !important;
                    text-transform: uppercase !important;
                    font-weight: 700 !important;
                    color: #64748b !important;
                    letter-spacing: 0.5px !important;
                    margin-bottom: 6px !important;
                    display: block !important;
                }
                
                .form-control-custom {
                    border: 1px solid #cbd5e1 !important;
                    border-radius: 0 !important;
                    font-size: 0.88rem !important;
                    padding: 8px 12px !important;
                }
                
                .form-control-custom:focus {
                    border-color: #ed3237 !important;
                    box-shadow: 0 0 0 3px rgba(237, 50, 55, 0.1) !important;
                    outline: none !important;
                }
            `}</style>
        </Fragment>
    );
}

export default EditProductPage;
